import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError, NOT_MODIFIED, type Aircraft, type MissionRow, type Pilot } from "../lib/api";
import { usePolling } from "../lib/usePolling";
import { useAuth } from "../lib/auth";
import { ChevronRight, Download, Droplet, Plane, Search } from "lucide-react";
import { Alert, Button, Input, PageTitle, Panel, Pill, Select } from "../components/ui";
import { amount, dayAndMonth } from "../lib/missionFormat";
import { formatHours } from "../lib/rounding";

const PAGE = 50;

type Filters = { q: string; aircraft: string; pilot: string; from: string; to: string; status: string };

/** A date from a date input, as the instant that local day starts (or, with `dayAfter`, the next one starts). */
function localDay(value: string, dayAfter = false): string | null {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d + (dayAfter ? 1 : 0)).toISOString();
}

/**
 * The query the server understands. Dates are whole local days, and the end day
 * is included: "to 12 Oct" means up to the end of the 12th, not its first second.
 */
function serverQuery(f: Filters, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.aircraft) p.set("aircraft", f.aircraft);
  if (f.pilot) p.set("pilot", f.pilot);
  const from = f.from && localDay(f.from);
  const to = f.to && localDay(f.to, true);
  if (from) p.set("from", from);
  if (to) p.set("to", to);
  if (f.status && f.status !== "all") p.set("status", f.status);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const text = p.toString();
  return text ? `?${text}` : "";
}

function summarise(rows: MissionRow[], hasMore: boolean): string {
  const flying = rows.filter((m) => m.endedAt === null).length;
  const parts = [`${rows.length}${hasMore ? "+" : ""} mission${rows.length === 1 && !hasMore ? "" : "s"}`];
  if (flying > 0) parts.push(`${flying} in progress`);
  return parts.join(" · ");
}

export function Missions() {
  const auth = useAuth();
  const display = auth.status === "authenticated" ? auth.me.organization.reportFuelUnit : null;
  const [params, setParams] = useSearchParams();

  // The URL is the filter, so a filtered list can be bookmarked, shared, and survives a refresh.
  const filters: Filters = {
    q: params.get("q") ?? "",
    aircraft: params.get("aircraft") ?? "",
    pilot: params.get("pilot") ?? "",
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
    status: params.get("status") ?? "all",
  };
  const filtered = Boolean(filters.q || filters.aircraft || filters.pilot || filters.from || filters.to || filters.status !== "all");

  const [rows, setRows] = useState<MissionRow[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [error, setError] = useState<string | null>(null);
  const [fleet, setFleet] = useState<Aircraft[]>([]);
  const [roster, setRoster] = useState<Pilot[]>([]);
  // The search box types faster than the server should be asked.
  const [typed, setTyped] = useState(filters.q);

  const set = (patch: Partial<Filters>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v && !(k === "status" && v === "all")) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
    setLimit(PAGE);
  };

  useEffect(() => {
    api.listAircraft().then((r) => setFleet(r.aircraft), () => undefined);
    api.listPilots().then((r) => setRoster(r.pilots), () => undefined);
  }, []);

  useEffect(() => {
    if (typed === filters.q) return;
    const t = window.setTimeout(() => set({ q: typed }), 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);

  // The address bar can change under the box (Clear, back button).
  useEffect(() => setTyped(filters.q), [filters.q]);

  const query = useMemo(
    () => serverQuery(filters, { limit: String(limit) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.q, filters.aircraft, filters.pilot, filters.from, filters.to, filters.status, limit],
  );

  // Answers can arrive out of order when filters change quickly; only the newest counts.
  const latest = useRef(0);
  const shown = useRef("");

  const load = async () => {
    const mine = ++latest.current;
    // A different query than what is on screen must never be answered with "unchanged".
    const fresh = shown.current !== query;
    try {
      const res = await api.listMissions(query, fresh);
      if (mine !== latest.current || res === NOT_MODIFIED) return;
      shown.current = query;
      setRows(res.missions);
      setHasMore(res.hasMore);
      setError(null);
    } catch (err) {
      // A failed refresh keeps whatever is on screen — stale beats empty.
      if (mine === latest.current && (rows === null || fresh)) {
        setError(err instanceof ApiError ? err.message : "Couldn't load missions.");
      }
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  usePolling(load, 30_000);

  const stale = rows !== null && shown.current !== query;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const csvHref = `/v1/missions.csv${serverQuery(filters, { tz: timeZone })}`;
  const clear = () => {
    setTyped("");
    setParams(new URLSearchParams(), { replace: true });
    setLimit(PAGE);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <PageTitle detail={rows && rows.length > 0 ? summarise(rows, hasMore) : undefined}>Missions</PageTitle>
        <a
          href={csvHref}
          download
          className="h-11 px-5 inline-flex items-center gap-2 rounded-lg font-semibold text-[0.95rem] bg-panel-raised text-ink hover:brightness-110"
        >
          <Download size={18} strokeWidth={2.4} aria-hidden />
          Export CSV
        </a>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-[minmax(12rem,2fr)_repeat(5,minmax(0,1fr))]">
        <div className="relative col-span-2 lg:col-span-3 xl:col-span-1">
          <Search size={18} strokeWidth={2.3} aria-hidden className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input
            type="search"
            aria-label="Search missions"
            placeholder="Mission, aircraft, pilot, task"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="w-full pl-10"
          />
        </div>
        <Select aria-label="Aircraft" value={filters.aircraft} onChange={(e) => set({ aircraft: e.target.value })}>
          <option value="">All aircraft</option>
          {fleet.map((a) => (
            <option key={a.id} value={a.id}>{a.registration}</option>
          ))}
        </Select>
        {/* Only offered once a roster exists; missions carry the typed name either way. */}
        {(roster.length > 0 || filters.pilot) && (
          <Select aria-label="Pilot" value={filters.pilot} onChange={(e) => set({ pilot: e.target.value })}>
            <option value="">All pilots</option>
            {roster.map((p) => (
              <option key={p.id} value={p.id}>{p.displayName}{p.archivedAt ? " (archived)" : ""}</option>
            ))}
          </Select>
        )}
        <Select aria-label="Status" value={filters.status} onChange={(e) => set({ status: e.target.value })}>
          <option value="all">Any status</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </Select>
        <Input type="date" aria-label="From date" value={filters.from} max={filters.to || undefined}
          onChange={(e) => set({ from: e.target.value })} />
        <Input type="date" aria-label="To date" value={filters.to} min={filters.from || undefined}
          onChange={(e) => set({ to: e.target.value })} />
      </div>

      {error && <Alert>{error}</Alert>}

      <Panel className={stale ? "opacity-60 transition-opacity" : "transition-opacity"}>
        {rows === null ? (
          <p className="p-6 text-[0.95rem] text-ink-soft">Loading…</p>
        ) : rows.length === 0 && filtered ? (
          <div className="p-10 text-center">
            <p className="text-[1.1rem] font-semibold text-ink mb-1.5">No missions match</p>
            <p className="text-[0.95rem] text-ink-soft mb-5">Nothing fits those filters.</p>
            <Button variant="secondary" onClick={clear}>Clear filters</Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <span className="inline-grid place-items-center w-14 h-14 rounded-lg bg-accent/20 text-accent-text mb-4">
              <Plane size={30} strokeWidth={2.2} aria-hidden />
            </span>
            <p className="text-[1.1rem] font-semibold text-ink mb-1.5">No missions yet</p>
            <p className="text-[0.95rem] text-ink-soft max-w-sm mx-auto">
              Missions appear here as they're recorded, within a minute of an iPad
              having signal.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {rows.map((m) => (
              <li key={m.id}>
                <Link
                  to={`/app/missions/${m.id}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-panel-raised transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="text-[1.05rem] font-semibold text-ink">{m.missionName}</span>
                      <span className="font-mono font-semibold text-[0.95rem] text-ink-soft">
                        {m.aircraftRegistration ?? m.aircraftCallsign ?? "—"}
                      </span>
                      {m.endedAt === null && <Pill className="bg-good/15 text-good">In progress</Pill>}
                    </div>

                    <div className="mt-1.5 flex gap-x-5 gap-y-1 flex-wrap text-[0.9rem] text-ink-soft tabular-nums">
                      <span>{dayAndMonth(m.startedAt)}</span>
                      <span className="inline-flex items-center gap-1">
                        <Plane size={15} strokeWidth={2.3} aria-hidden />
                        {formatHours(m.reportedAirMinutes, m.reportedAirDp)} h
                      </span>
                      <span>{formatHours(m.reportedEngineMinutes, m.reportedEngineDp)} h engine</span>
                      <span className="inline-flex items-center gap-1">
                        <Droplet size={15} strokeWidth={2.3} aria-hidden />
                        {m.dropCount} drops
                      </span>
                      <span>{amount(m.fuelBurnt, m.fuelUnit, display ?? m.fuelUnit)} burnt</span>
                      {m.pilotName && <span>{m.pilotName}</span>}
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-ink-faint shrink-0" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {rows && hasMore && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => setLimit((n) => n + PAGE)}>
            Show more
          </Button>
        </div>
      )}
    </div>
  );
}
